'use client';

import React, { useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import StackedHero from '@/components/our-work/StackedHero';

type Project = {
    name: string;
    tag: string;
    href: string;
    image?: string;
    customThumbnail?: React.ReactNode;
};

/* ── Design1Studio Instagram card ── */
function Design1StudioInstagramCard() {
    return (
        <div style={{
            width: '100%', height: '100%',
            background: '#0e0e16',
            display: 'flex', flexDirection: 'column',
            padding: 'clamp(16px, 2vw, 28px)',
            boxSizing: 'border-box',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        }}>
            {/* Top bar */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'clamp(14px, 2vw, 24px)', flexShrink: 0 }}>
                <svg width="clamp(14px,1.4vw,20px)" height="clamp(14px,1.4vw,20px)" viewBox="0 0 20 20" fill="none">
                    <path d="M13 4L7 10L13 16" stroke="#e8e8e8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span style={{ color: '#e8e8e8', fontSize: 'clamp(11px, 1.1vw, 16px)', fontWeight: 600 }}>design1studio_interiors</span>
                <div style={{ display: 'flex', gap: 3 }}>
                    {[0,1,2].map(i => <div key={i} style={{ width: 4, height: 4, borderRadius: '50%', background: '#e8e8e8' }} />)}
                </div>
            </div>

            {/* Profile row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(12px, 1.5vw, 20px)', marginBottom: 'clamp(10px, 1.2vw, 16px)', flexShrink: 0 }}>
                <div style={{
                    width: 'clamp(48px, 5.5vw, 76px)', height: 'clamp(48px, 5.5vw, 76px)',
                    borderRadius: '50%', background: '#f0ebe3',
                    flexShrink: 0, overflow: 'hidden',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
                }}>
                    <Image src="/images/our-work/d1s-instagram-avatar.webp" alt="Design1Studio logo" fill style={{ objectFit: 'cover' }} sizes="76px" />
                </div>
                <div style={{ display: 'flex', gap: 'clamp(10px, 2vw, 28px)', flex: 1 }}>
                    {[{ value: '341', label: 'posts' }, { value: '4,055', label: 'followers' }, { value: '154', label: 'following' }].map(({ value, label }) => (
                        <div key={label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <span style={{ color: '#ffffff', fontSize: 'clamp(12px, 1.3vw, 18px)', fontWeight: 700, lineHeight: 1.2 }}>{value}</span>
                            <span style={{ color: '#a0a0a8', fontSize: 'clamp(9px, 0.85vw, 12px)' }}>{label}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Name */}
            <p style={{ color: '#ffffff', fontSize: 'clamp(11px, 1.05vw, 15px)', fontWeight: 600, margin: '0 0 clamp(4px, 0.5vw, 8px)', lineHeight: 1.3 }}>
                Design1studio | Interior | Design Experts in Bangalore
            </p>

            {/* Category */}
            <p style={{ color: '#a0a0a8', fontSize: 'clamp(9px, 0.85vw, 12px)', margin: '0 0 clamp(6px, 0.7vw, 10px)' }}>Interior design studio</p>

            {/* Bio */}
            <div style={{ marginBottom: 'clamp(6px, 0.7vw, 10px)' }}>
                {['creating spaces that feel effortless', '45-Day Dream Home Transformation', '10-Year Warranty | 103+ Success Stories', '📞 +91-9606981151'].map((line, i) => (
                    <p key={i} style={{ color: '#e8e8e8', fontSize: 'clamp(9px, 0.9vw, 13px)', margin: '0 0 2px', lineHeight: 1.45 }}>{line}</p>
                ))}
            </div>

            {/* Location */}
            <p style={{ color: '#6a9fd8', fontSize: 'clamp(8px, 0.8vw, 12px)', margin: 0 }}>Doddakalsandra, Bangalore, India</p>
        </div>
    );
}

/* ── KOZA Instagram card ── */
function KozaInstagramCard() {
    return (
        <div style={{
            width: '100%', height: '100%',
            background: '#0e0e16',
            display: 'flex', flexDirection: 'column',
            padding: 'clamp(16px, 2vw, 28px)',
            boxSizing: 'border-box',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        }}>
            {/* Top bar */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'clamp(14px, 2vw, 24px)', flexShrink: 0 }}>
                <svg width="clamp(14px,1.4vw,20px)" height="clamp(14px,1.4vw,20px)" viewBox="0 0 20 20" fill="none">
                    <path d="M13 4L7 10L13 16" stroke="#e8e8e8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span style={{ color: '#e8e8e8', fontSize: 'clamp(11px, 1.1vw, 16px)', fontWeight: 600 }}>koza.aesthetic.care</span>
                <div style={{ display: 'flex', gap: 3 }}>
                    {[0,1,2].map(i => <div key={i} style={{ width: 4, height: 4, borderRadius: '50%', background: '#e8e8e8' }} />)}
                </div>
            </div>

            {/* Profile row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(12px, 1.5vw, 20px)', marginBottom: 'clamp(10px, 1.2vw, 16px)', flexShrink: 0 }}>
                <div style={{
                    width: 'clamp(48px, 5.5vw, 76px)', height: 'clamp(48px, 5.5vw, 76px)',
                    borderRadius: '50%', background: '#f5fde9', border: '2.5px solid #e0c58f',
                    flexShrink: 0, overflow: 'hidden',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
                }}>
                    <Image src="/images/our-work/koza-instagram-avatar.webp" alt="KOZA Aesthetic Care logo" fill style={{ objectFit: 'cover' }} sizes="76px" />
                </div>
                <div style={{ display: 'flex', gap: 'clamp(10px, 2vw, 28px)', flex: 1 }}>
                    {[{ value: '490', label: 'posts' }, { value: '12.1K', label: 'followers' }, { value: '91', label: 'following' }].map(({ value, label }) => (
                        <div key={label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <span style={{ color: '#ffffff', fontSize: 'clamp(12px, 1.3vw, 18px)', fontWeight: 700, lineHeight: 1.2 }}>{value}</span>
                            <span style={{ color: '#a0a0a8', fontSize: 'clamp(9px, 0.85vw, 12px)' }}>{label}</span>
                        </div>
                    ))}
                </div>
            </div>

            <p style={{ color: '#ffffff', fontSize: 'clamp(11px, 1.05vw, 15px)', fontWeight: 600, margin: '0 0 clamp(4px, 0.5vw, 8px)', lineHeight: 1.3 }}>Koza Aesthetic Care</p>
            <p style={{ color: '#a0a0a8', fontSize: 'clamp(9px, 0.85vw, 12px)', margin: '0 0 clamp(6px, 0.7vw, 10px)' }}>Health/beauty</p>
            <div style={{ marginBottom: 'clamp(6px, 0.7vw, 10px)' }}>
                <p style={{ color: '#e8e8e8', fontSize: 'clamp(9px, 0.9vw, 13px)', margin: '0 0 2px', lineHeight: 1.45 }}>Advanced Skin | Hair | Slimming | Cosmetic Procedures</p>
                <p style={{ color: '#e8e8e8', fontSize: 'clamp(9px, 0.9vw, 13px)', margin: 0, lineHeight: 1.45 }}>Dermatologist-backed Solutions</p>
            </div>
            <p style={{ color: '#a0a0a8', fontSize: 'clamp(8px, 0.8vw, 12px)', margin: '0 0 clamp(4px, 0.5vw, 8px)', lineHeight: 1.4 }}>📍 Banashankari, Ecity, Jayanagar, Arekere, HRBR Layout, Sarjapur</p>
            <p style={{ color: '#6a9fd8', fontSize: 'clamp(8px, 0.8vw, 12px)', margin: 0 }}>🔗 linktr.ee/KozaAestheticCare</p>
        </div>
    );
}

/* ── DCPL LinkedIn card ── */
function DCPLLinkedInCard() {
    return (
        <div style={{
            width: '100%', height: '100%',
            background: '#1b1f23',
            display: 'flex', flexDirection: 'column',
            padding: 'clamp(16px, 2vw, 28px)',
            boxSizing: 'border-box',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        }}>
            {/* Top bar – LinkedIn style */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'clamp(14px, 2vw, 24px)', flexShrink: 0 }}>
                <svg width="clamp(14px,1.4vw,20px)" height="clamp(14px,1.4vw,20px)" viewBox="0 0 20 20" fill="none">
                    <path d="M13 4L7 10L13 16" stroke="#e8e8e8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span style={{ color: '#e8e8e8', fontSize: 'clamp(11px, 1.1vw, 16px)', fontWeight: 600 }}>Digital Circuits Pvt Ltd</span>
                <div style={{ display: 'flex', gap: 3 }}>
                    {[0,1,2].map(i => <div key={i} style={{ width: 4, height: 4, borderRadius: '50%', background: '#e8e8e8' }} />)}
                </div>
            </div>

            {/* Profile row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(12px, 1.5vw, 20px)', marginBottom: 'clamp(10px, 1.2vw, 16px)', flexShrink: 0 }}>
                {/* LinkedIn logo avatar */}
                <div style={{
                    width: 'clamp(48px, 5.5vw, 76px)', height: 'clamp(48px, 5.5vw, 76px)',
                    borderRadius: 8, background: '#0a66c2',
                    flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <span style={{ color: '#fff', fontWeight: 800, fontSize: 'clamp(18px, 2vw, 28px)', fontFamily: 'Georgia, serif', letterSpacing: '-1px' }}>in</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ color: '#ffffff', fontSize: 'clamp(11px, 1.1vw, 15px)', fontWeight: 700 }}>Digital Circuits Pvt Ltd</span>
                    <span style={{ color: '#a0a0a8', fontSize: 'clamp(9px, 0.85vw, 12px)' }}>Electronics Manufacturing</span>
                </div>
            </div>

            {/* Campaign metrics */}
            <div style={{
                background: 'rgba(10,102,194,0.12)',
                border: '1px solid rgba(10,102,194,0.3)',
                borderRadius: 8,
                padding: 'clamp(8px, 1vw, 14px)',
                marginBottom: 'clamp(8px, 1vw, 14px)',
                flexShrink: 0,
            }}>
                <p style={{ color: '#7cb9ff', fontSize: 'clamp(8px, 0.8vw, 11px)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>B2B Email Campaign · 1 Month</p>
                <div style={{ display: 'flex', gap: 'clamp(12px, 2vw, 32px)' }}>
                    {[{ value: '1,955', label: 'emails sent' }, { value: '1,261', label: 'opens' }, { value: '64.5%', label: 'open rate' }].map(({ value, label }) => (
                        <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <span style={{ color: '#ffffff', fontSize: 'clamp(12px, 1.3vw, 18px)', fontWeight: 700 }}>{value}</span>
                            <span style={{ color: '#a0a0a8', fontSize: 'clamp(8px, 0.75vw, 11px)' }}>{label}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Services */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {['B2B Email Outreach', 'LinkedIn Marketing', 'Lead Generation'].map(tag => (
                    <span key={tag} style={{
                        background: 'rgba(10,102,194,0.2)',
                        color: '#7cb9ff',
                        fontSize: 'clamp(8px, 0.75vw, 11px)',
                        padding: '3px 8px',
                        borderRadius: 20,
                        border: '1px solid rgba(10,102,194,0.3)',
                        whiteSpace: 'nowrap',
                    }}>{tag}</span>
                ))}
            </div>
        </div>
    );
}

const projects: Project[] = [
    {
        name: 'Design1Studio',
        tag: 'Real stories',
        href: '/our-work/social-media-marketing/design1studio',
        customThumbnail: <Design1StudioInstagramCard />,
    },
    {
        name: 'KOZA',
        tag: 'Real stories',
        href: '/our-work/social-media-marketing/koza',
        customThumbnail: <KozaInstagramCard />,
    },
    {
        name: 'Digital Circuits',
        tag: 'B2B Growth',
        href: '/our-work/social-media-marketing/dcpl',
        customThumbnail: <DCPLLinkedInCard />,
    },
];

export default function SocialMediaMarketingPage() {
    useEffect(() => {
        document.title = 'Social Media Marketing | Lead Workz';
    }, []);

    return (
        <main style={{ background: '#0f0f0f', minHeight: '100vh', overflow: 'clip' }}>

            <StackedHero
                title="Social Media Marketing"
                description="We create and manage social media that goes beyond posting — it performs. From content creation to campaign execution, we help brands build a strong presence, engage their audience and drive meaningful results."
            >
                <div style={{
                    padding: 'clamp(48px, 6vw, 96px) clamp(20px, 4vw, 60px) clamp(64px, 8vw, 120px)',
                    margin: '0 auto',
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    width: '100%',
                    maxWidth: 1920,
                    boxSizing: 'border-box',
                    gap: 50,
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                }}>
                    {projects.map((project) => (
                        <ProjectCard key={project.name} project={project} />
                    ))}
                </div>
            </StackedHero>

        </main>
    );
}

function ProjectCard({ project }: { project: Project }) {
    const cardRef = useRef<HTMLDivElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 28, flex: '1 1 360px', maxWidth: 560, minWidth: 280 }}>
            <Link
                href={project.href}
                style={{ display: 'block', textDecoration: 'none' }}
                onMouseEnter={() => { if (cardRef.current) cardRef.current.style.transform = 'scale(1.03)'; if (imgRef.current) imgRef.current.style.transform = 'scale(1.03)'; }}
                onMouseLeave={() => { if (cardRef.current) cardRef.current.style.transform = 'scale(1)'; if (imgRef.current) imgRef.current.style.transform = 'scale(1)'; }}
            >
                <div style={{ width: '100%', height: 'clamp(280px, 26vw, 500px)', borderRadius: 32, overflow: 'hidden', background: '#0e0e16', position: 'relative' }}>
                    {project.customThumbnail ? (
                        <div ref={cardRef} style={{ width: '100%', height: '100%', transition: 'transform 0.6s ease' }}>
                            {project.customThumbnail}
                        </div>
                    ) : (
                        <Image src={project.image!} alt={project.name}
                            fill style={{ objectFit: 'cover', objectPosition: 'top center', transition: 'transform 0.6s ease' }} sizes="(max-width: 768px) 100vw, 560px" />
                    )}
                </div>
            </Link>

            <div style={{ display: 'flex', alignItems: 'center', gap: 20, paddingLeft: 8 }}>
                <Link href={project.href} style={{ textDecoration: 'none' }}>
                    <span style={{ fontFamily: '"Clash Display", sans-serif', fontWeight: 500, fontSize: 'clamp(18px, 2.2vw, 42px)', color: '#080400', letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>
                        {project.name}
                    </span>
                </Link>
                <svg width="40" height="70" viewBox="0 0 50 87" fill="none" style={{ flexShrink: 0 }}>
                    <line x1="45" y1="5" x2="5" y2="82" stroke="#080400" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <span style={{ fontFamily: 'var(--font-manrope)', fontWeight: 400, fontSize: 'clamp(13px, 1.1vw, 20px)', color: '#080400', letterSpacing: '0.02em' }}>
                    {project.tag}
                </span>
            </div>
        </div>
    );
}
